import { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  useToast,
  useTranslator,
  useAppContext,
  cloudflareImage,
  mealPlaceholderImage,
} from '@front-activerse/common-components';
import { Link, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

const FavMeal = ({ meal, removeMealFromFavorites, addMealToFavorites, toggleInsertDate }) => {
  const navigate = useNavigate();
  const { toastSuccess, toastError } = useToast();
  const { t } = useTranslator();
  const { trackFirebaseEvent } = useOutletContext();
  const [showModal, setShowModal] = useState(false);

  const {
    consts: { APP_BRAND },
    images: { favouriteFull, addToMealplan, addToTrackerIcon },
  } = useAppContext();

  const handleRemoveMeal = async () => {
    try {
      const response = await removeMealFromFavorites.removeMeal(meal.id);
      if (response.data.me.removeMealFromFavoritesByMealId.__typename === 'BasicMutationSuccess') {
        toastSuccess(
          <p>
            {t('mealPlan.favorites.notification.removedFromFav.content')}
            <Button color="link" onClick={() => handleAddMeal(meal.id)}>
              {t('meal.favorites.notification.removedFromFav.button')}
            </Button>
          </p>
        );

        trackFirebaseEvent('user_action', {
          action: 'tap-delete_favourites',
          location: 'V_favourites',
          meal_id: meal.id,
          meal_category: meal.name,
        });
      }
    } catch (error) {
      toastError();
      throw new Error(`Failed to remove meal from favorites: ${error}`);
    }
  };

  const handleAddMeal = async (mealId) => {
    try {
      await addMealToFavorites.addMeal(mealId);
    } catch (error) {
      toastError();
      throw new Error(`Failed to add meal to favorites: ${error}`);
    }
  };

  const renderMealImage = () => {
    let mealImg;
    if (meal.preparationImageUrl) {
      mealImg = meal.preparationImageUrl;
    } else {
      mealImg = mealPlaceholderImage(meal.mealType, APP_BRAND);
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'cookbookMealItemVertical');

    return <img src={coverImage} className="img-fluid" alt="" />;
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleAddMealToTracker = () => {
    const isTrackerEnabled = localStorage.getItem('trackerEnabled') === 'true';

    if (isTrackerEnabled) {
      navigate(`/meals/day-plan/module/tracker/add-fav-meal/${meal.id}`);
    } else {
      toggleModal();
    }
  };

  return (
    <>
      <div className="meal">
        <div className="meal-box cursor-pointer">
          <div
            className="meal-image"
            onClick={() => navigate(`/meals/day-plan/module/favourite-meals/${meal.id}`)}
          >
            {renderMealImage()}
          </div>

          <div
            className="tags"
            onClick={() => navigate(`/meals/day-plan/module/favourite-meals/${meal.id}`)}
          >
            {meal.mealCustomTag && meal.mealCustomTag.label && (
              <span
                className="tag me-1"
                style={{ backgroundColor: meal.mealCustomTag.color, color: '#fff' }}
              >
                {meal.mealCustomTag.label}
              </span>
            )}
            <span className="tag me-1">{meal.name}</span>
          </div>

          <div
            className="meal-title mb-s"
            onClick={() => navigate(`/meals/day-plan/module/favourite-meals/${meal.id}`)}
          >
            <p className="semi-bold mb-0">{meal.publicName}</p>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <span className="description text-text-variant-color">{meal.kcal}&nbsp;kcal</span>
            <div>
              <Button
                color="link"
                size="sm"
                className="p-xs z-index-999 me-s"
                style={{ minHeight: '32px', lineHeight: '0' }}
                onClick={() => toggleInsertDate(meal.id)}
              >
                <img src={addToMealplan} alt="" />
              </Button>

              <Button
                color="primary"
                size="sm"
                className="p-xs z-index-999 tracker rounded"
                style={{ minHeight: '32px', lineHeight: '0' }}
                onClick={handleAddMealToTracker}
              >
                <img src={addToTrackerIcon} alt="" />
              </Button>
            </div>
          </div>
        </div>
        <button className="add-remove-fav" type="button" onClick={handleRemoveMeal}>
          <img src={favouriteFull} alt="" width="32" height="32" />
        </button>
      </div>

      <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold">{t('tracker.logMealError.popup.title')}</h4>
          <p className="text-text-variant-color multi-line mb-0">
            {t('tracker.logMealError.popup.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            tag={Link}
            to="/tracker/settings"
            className="w-100"
            onClick={toggleModal}
          >
            {t('tracker.logMealError.popup.settingsButton')}
          </Button>
          <Button color="link" className="w-100" onClick={toggleModal}>
            {t('tracker.logMealError.popup.cancelButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FavMeal;
